import { useMemo } from "react";
import { Polygon, Tooltip } from "react-leaflet";
import type { LatLngTuple } from "leaflet";
import { useQuery } from "@tanstack/react-query";

import { telemetryAjax } from "../../ajax/TelemetryAjax";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { DigBlockFenceDTO } from "../../DTOs/Telemetry/DigBlockFenceDTO";
import { MINIMUM_VERTICES, formatAreaMetres, parseGeofencePolygon, polygonAreaAndPerimeterMetres } from "./geofencePolygon";

/**
 * Draws the LIVE dig blocks - the ones still being mined - beneath the fleet.
 *
 * Note: a third colour, distinct from both the location fences and the blast patterns, because all three
 * routinely overlap: a dig block sits inside a blast pattern which sits inside a pit, and each of those
 * already has a fence drawn. Cool against the blast patterns' warm brown so the two separate at a glance
 * even where their outlines nearly coincide.
 *
 * Note: WHY BOTH LAYERS MATTER TOGETHER. Ore Control own the dig-block outlines and Drill & Blast own the
 * blast-pattern ones, and the two are drawn independently on purpose - comparing them is the reconciliation
 * the Ore Tally reports numerically. Seeing them on one map, over where the machines actually drove, is the
 * same comparison made visible.
 *
 * Note: "live" is decided by the SERVER (Completed = false), not here. Finished blocks accumulate forever -
 * 6,740 blocks against 67 not completed on the test database - so filtering client-side would mean shipping
 * the whole history down the wire to throw almost all of it away.
 *
 * Note: blocks on different FLITCHES share a footprint, because a flitch is a vertical sub-bench stacked
 * over the same plan-view ground. Two outlines sitting exactly on top of each other is therefore expected,
 * not a fault, which is why the tooltip names the flitch - it is the only thing distinguishing them.
 */

// Note: muted teal. Cool against the blast patterns' #B0651F, and still restrained - these are context for
// reading the fleet, not the subject of the map.
const DIG_BLOCK_COLOUR = "#2F7D72";

export const DIG_BLOCK_FENCE_QUERY_KEY = ["telemetry", "digBlockFences"];

type DigBlockFenceLayerProps = {
  visible: boolean;
};

export const DigBlockFenceLayer: React.FC<DigBlockFenceLayerProps> = ({ visible }) => {
  const { data: fences = [] } = useQuery({
    queryKey: DIG_BLOCK_FENCE_QUERY_KEY,
    queryFn: async () => {
      const res = await telemetryAjax.getLiveDigBlockFences();
      // Note: this endpoint returns the array directly rather than a PagedResult, matching the blast
      // pattern fences - so the error guard is the only unwrapping needed.
      if (ErrorResponse.isErrorResponse(res)) return [] as DigBlockFenceDTO[];
      return res;
    },
    // Note: the same 15 minutes the blast pattern fences use, and for the same reason. A block's outline
    // changes when someone draws it or marks it completed, neither of which happens while a dispatcher
    // watches the map.
    staleTime: 15 * 60 * 1000,
  });

  const shapes = useMemo(() => fences.flatMap((fence: DigBlockFenceDTO) => {
    const { vertices } = parseGeofencePolygon(fence.geofencePolygon);

    // Note: a polygon that will not parse is skipped rather than drawn wrong. The server logs it by block
    // id when inference hits the same row, so it is diagnosable there rather than here.
    if (vertices.length < MINIMUM_VERTICES) return [];

    return [{
      id: fence.geoOreBlockId,
      oreBlock: fence.oreBlock,
      flitch: fence.flitch,
      positions: vertices.map(v => [v.lat, v.lng] as LatLngTuple),
      measurements: polygonAreaAndPerimeterMetres(vertices),
    }];
  }), [fences]);

  if (!visible) return null;

  return (
    <>
      {shapes.map(shape => (
        <Polygon
          key={`digblock-${shape.id}`}
          positions={shape.positions}
          pathOptions={{ color: DIG_BLOCK_COLOUR, weight: 1.5, opacity: 0.8, fillOpacity: 0.08 }}
        >
          <Tooltip>
            {shape.oreBlock} - dig block, flitch {shape.flitch}
            {shape.measurements && ` - ${formatAreaMetres(shape.measurements.areaSqM)}, ${Math.round(shape.measurements.perimeterM).toLocaleString()} m perimeter`}
          </Tooltip>
        </Polygon>
      ))}
    </>
  );
};

export default DigBlockFenceLayer;
